import React, { useState } from 'react';
import { appStorage } from '../../../firebase.config';
import { auth } from '../../../firebase.config';
import { useEnhancedCompany } from '../../../contexts/EnhancedCompanyContext';
import SignaturePad from '../components/SignaturePad';
import CollapsibleInfo from '../components/CollapsibleInfo';

const DocumentsStep = ({ data, errors, onChange, onTouch }) => {
  const { currentCompany } = useEnhancedCompany();
  const [uploading, setUploading] = useState({
    logo: false,
    seal: false,
    signature: false
  });
  
  const [uploadProgress, setUploadProgress] = useState({
    logo: 0,
    seal: 0,
    signature: 0
  });

  const [previews, setPreviews] = useState({
    logo: data.logoUrl || '',
    seal: data.sealUrl || '',
    signature: data.signUrl || ''
  });

  // Update previews when data changes (for edit mode)
  React.useEffect(() => {
    setPreviews({
      logo: data.logoUrl || '',
      seal: data.sealUrl || '',
      signature: data.signUrl || ''
    });
  }, [data.logoUrl, data.sealUrl, data.signUrl]);

  const [showSignaturePad, setShowSignaturePad] = useState(false);

  // Helper function to save base64 to Firestore
  const saveBase64ToFirestore = async (base64Data, companyId, category, fileName) => {
    try {
      const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../../../firebase.config');
      
      const docRef = doc(db, 'companies', companyId);
      const data = {
        [`${category}Base64`]: base64Data,
        [`${category}FileName`]: fileName,
        [`${category}UploadDate`]: serverTimestamp(),
        lastUpdated: serverTimestamp()
      };
      
      await setDoc(docRef, data, { merge: true });
      console.log('✅ Base64 data saved to Firestore');
    } catch (error) {
      console.error('❌ Failed to save base64 to Firestore:', error);
      throw error;
    }
  };

  const handleFileUpload = async (type, file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (PNG, JPG, JPEG)');
      return;
    }

    // Validate file size (1MB max)
    if (file.size > 1 * 1024 * 1024) {
      alert('File size should be less than 1MB');
      return;
    }

    setUploading(prev => ({ ...prev, [type]: true }));
    setUploadProgress(prev => ({ ...prev, [type]: 0 }));

    try {
      console.log('📤 Uploading file to Firebase Storage:', { 
        type, 
        fileName: file.name, 
        size: file.size, 
        fileType: file.type,
        user: auth.currentUser?.uid,
        companyId: currentCompany?.companyId || currentCompany?.id
      });
      
      // Check if user is authenticated
      if (!auth.currentUser) {
        throw new Error('User not authenticated. Please login again.');
      }

      const companyId = currentCompany?.companyId || currentCompany?.id || auth.currentUser?.uid;
      const category = type === 'signature' ? 'signatures' : type + 's';
      
      // Upload to Firebase Storage
      setUploadProgress(prev => ({ ...prev, [type]: 50 }));
      const firebaseURL = await appStorage.uploadCompanyAsset(file, companyId, category);
      
      // Update progress
      setUploadProgress(prev => ({ ...prev, [type]: 100 }));
      
      // Update preview and form data
      setPreviews(prev => ({ ...prev, [type]: firebaseURL }));
      
      const fieldMap = {
        logo: 'logoUrl',
        seal: 'sealUrl',
        signature: 'signUrl'
      };
      
      onChange(fieldMap[type], firebaseURL);
      onTouch(fieldMap[type]);
      
      console.log('✅ Firebase upload successful:', firebaseURL);
      
      // Reset progress after delay
      setTimeout(() => {
        setUploadProgress(prev => ({ ...prev, [type]: 0 }));
      }, 1000);

    } catch (error) {
      console.error('❌ Firebase upload failed:', error);
      
      // Try base64 fallback for any error
      console.log('🔄 Upload failed, trying base64 fallback...');
      try {
        const base64Data = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(file);
        });
        
        console.log('✅ Base64 fallback successful');
        
        // Save base64 to Firebase Firestore for cross-device access
        try {
          const companyId = currentCompany?.companyId || currentCompany?.id || auth.currentUser?.uid;
          await saveBase64ToFirestore(base64Data, companyId, type, file.name);
          console.log('✅ Base64 saved to Firestore for cross-device access');
        } catch (firestoreError) {
          console.warn('⚠️ Could not save to Firestore, using local storage only:', firestoreError);
        }
        
        // Update preview and form data with base64
        setPreviews(prev => ({ ...prev, [type]: base64Data }));
        
        const fieldMap = {
          logo: 'logoUrl',
          seal: 'sealUrl',
          signature: 'signUrl'
        };
        
        onChange(fieldMap[type], base64Data);
        onTouch(fieldMap[type]);
        
        setUploadProgress(prev => ({ ...prev, [type]: 100 }));
        
        // Reset progress after delay
        setTimeout(() => {
          setUploadProgress(prev => ({ ...prev, [type]: 0 }));
        }, 1000);
        
        alert('Upload completed using offline mode. File will be available on all devices.');
        
      } catch (fallbackError) {
        console.error('❌ Base64 fallback also failed:', fallbackError);
        alert(`Upload failed: ${error.message}. Please try again.`);
        setUploadProgress(prev => ({ ...prev, [type]: 0 }));
      }
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const removeFile = (type) => {
    const fieldMap = {
      logo: 'logoUrl',
      seal: 'sealUrl',
      signature: 'signUrl'
    };

    setPreviews(prev => ({ ...prev, [type]: '' }));
    onChange(fieldMap[type], '');
    onTouch(fieldMap[type]);
  };

  const handleSignatureSave = async (signatureDataURL) => {
    try {
      // Convert data URL to blob without using fetch
      const base64Data = signatureDataURL.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });
      
      // Create a file from the blob
      const file = new File([blob], `signature_${Date.now()}.png`, { type: 'image/png' });
      
      // Upload to Firebase Storage
      await handleFileUpload('signature', file);
      
    } catch (error) {
      console.error('❌ Signature upload failed:', error);
      // Fallback to data URL if Firebase fails
      setPreviews(prev => ({ ...prev, signature: signatureDataURL }));
      onChange('signUrl', signatureDataURL);
      onTouch('signUrl');
    }
  };

  const FileUploadCard = ({ type, title, description, icon, preview, isUploading, progress }) => {
    const inputId = `${type}-upload`;

    return (
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-gray-400 transition-colors">
        <div className="text-center">
          {preview ? (
            <div className="space-y-4">
              <div className="relative inline-block">
                <img
                  src={preview}
                  alt={title}
                  className="w-24 h-24 object-contain mx-auto rounded-lg border border-gray-200"
                />
                <button
                  onClick={() => removeFile(type)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div>
                <p className="text-sm font-medium text-green-600">✓ {title} uploaded</p>
                <button
                  onClick={() => document.getElementById(inputId).click()}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                >
                  Change file
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {isUploading ? (
                <div className="flex flex-col items-center space-y-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <div className="w-full">
                    <p className="text-sm text-gray-600 mb-1">Uploading {title}...</p>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${progress || 0}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{progress || 0}%</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-4xl mb-1">{icon}</div>
                  <div>
                    <label htmlFor={inputId} className="cursor-pointer">
                      <span className="mt-2 block text-sm font-medium text-gray-900">
                        Upload {title}
                      </span>
                      <span className="mt-1 block text-sm text-gray-500">
                        {description}
                      </span>
                    </label>
                  </div>
                </>
              )}
            </div>
          )}

          <input
            id={inputId}
            name={inputId}
            type="file"
            className="sr-only"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                handleFileUpload(type, file);
              }
            }}
            disabled={isUploading}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-1">Company Documents</h3>
        <p className="text-gray-600">Upload your company branding and signature documents</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {/* Company Logo */}
        <FileUploadCard
          type="logo"
          title="Company Logo"
          description="PNG, JPG up to 1MB"
          icon="🏢"
          preview={previews.logo}
          isUploading={uploading.logo}
          progress={uploadProgress.logo}
        />

        {/* Company Seal */}
        <FileUploadCard
          type="seal"
          title="Company Seal"
          description="PNG, JPG up to 1MB"
          icon="🔖"
          preview={previews.seal}
          isUploading={uploading.seal}
          progress={uploadProgress.seal}
        />

        {/* Digital Signature */}
        <FileUploadCard
          type="signature"
          title="Digital Signature"
          description="PNG, JPG up to 1MB"
          icon="✍️"
          preview={previews.signature}
          isUploading={uploading.signature}
          progress={uploadProgress.signature}
        />
      </div>

      {/* Digital Signature Option */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h4 className="font-medium text-blue-900 mb-2">Create Digital Signature</h4>
        <p className="text-sm text-blue-700 mb-2">
          Don't have a signature image? You can create one digitally by drawing with your mouse or finger.
        </p>
        <button
          onClick={() => setShowSignaturePad(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
        >
          Open Signature Pad
        </button>
      </div>

      {/* Collapsible Info Box */}
      <CollapsibleInfo title="Document Guidelines">
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>Logo:</strong> High-quality company logo for invoices and documents</li>
          <li>• <strong>Seal:</strong> Official company stamp/seal (if available)</li>
          <li>• <strong>Signature:</strong> Authorized signatory's signature for documents</li>
          <li>• All documents are optional but recommended for professional appearance</li>
          <li>• You can add or update these documents later from settings</li>
        </ul>
      </CollapsibleInfo>

      {/* Signature Pad Modal */}
      <SignaturePad
        isOpen={showSignaturePad}
        onClose={() => setShowSignaturePad(false)}
        onSave={handleSignatureSave}
      />
    </div>
  );
};

export default DocumentsStep;
